import { runAz } from "./az.js";

const ADMIN_DIRECTORY_ROLES = ["Global Administrator", "Application Administrator", "Cloud Application Administrator"];

/** Owner or User Access Administrator at subscription scope (inherited from a management group counts). */
export async function checkSubScopeAdmin(callerObjectId: string, subscriptionId: string): Promise<{ ok: boolean; role?: string }> {
  const rows = JSON.parse(await runAz([
    "role", "assignment", "list",
    "--assignee", callerObjectId,
    "--scope", `/subscriptions/${subscriptionId}`,
    "--include-inherited",
    "--query", "[].{roleDefinitionName: roleDefinitionName}",
    "-o", "json",
  ])) as { roleDefinitionName: string }[];
  const hit = rows.find((r) => r.roleDefinitionName === "Owner" || r.roleDefinitionName === "User Access Administrator");
  return { ok: Boolean(hit), role: hit?.roleDefinitionName };
}

/**
 * Directory-admin capability to register the m8t app reg. Uses the
 * guest-blind-safe roleManagement query (memberOf returns [] for guests).
 * A 403 (denied read) => inconclusive => require --client-id. See app-reg.ts.
 */
export async function checkAppRegCapability(callerObjectId: string): Promise<{ ok: boolean; inconclusive: boolean }> {
  let roles: string[] | null;
  try {
    roles = JSON.parse(await runAz([
      "rest", "--method", "GET",
      "--url", `https://graph.microsoft.com/v1.0/roleManagement/directory/roleAssignments?$filter=principalId eq '${callerObjectId}'&$expand=roleDefinition`,
      "--query", "value[].roleDefinition.displayName", "-o", "json",
    ])) as string[];
  } catch {
    roles = null; // read denied — inconclusive
  }
  if (roles === null) return { ok: false, inconclusive: true };
  return { ok: roles.some((r) => ADMIN_DIRECTORY_ROLES.includes(r)), inconclusive: false };
}

/** Idempotent — Microsoft.ContainerInstance is NOT registered by default in a fresh sub. */
export async function registerContainerInstance(subscriptionId: string): Promise<void> {
  await runAz(["provider", "register", "--namespace", "Microsoft.ContainerInstance", "--subscription", subscriptionId, "--only-show-errors"]);
}

export function buildPreflightBanner(who: { upn: string; tenantId: string; subscriptionId: string }): string {
  return [
    "╔══════════════════════════════════════════════════════════════════════╗",
    "║  ⛔  STOP — READ THIS BEFORE CONTINUING                                 ║",
    "║                                                                        ║",
    "║  Installing m8t creates real Azure resources in YOUR            ║",
    "║  subscription and assigns roles. To do that, the account you just     ║",
    "║  signed in with MUST be:                                              ║",
    "║                                                                        ║",
    "║    • OWNER or USER ACCESS ADMINISTRATOR at the subscription scope     ║",
    "║      (it has to create a managed identity and assign it roles), AND   ║",
    "║    • a directory admin able to register one Entra app                 ║",
    "║      (Application / Cloud Application / Global Administrator)          ║",
    "║      — OR you supply a ready app registration with --client-id.       ║",
    "║                                                                        ║",
    "║  If you are NOT this, this install CANNOT proceed and will STOP NOW.  ║",
    "║  Nothing has been created yet. Get an admin to run this, or ask one   ║",
    "║  for an app registration id (--client-id <appId>) and try again.      ║",
    "╚══════════════════════════════════════════════════════════════════════╝",
    "",
    `Signed in as:  ${who.upn}   (tenant ${who.tenantId}, sub ${who.subscriptionId})`,
    "",
  ].join("\n");
}
